import {
  Component,
  inject,
  Output,
  EventEmitter,
  NO_ERRORS_SCHEMA,
  input,
  WritableSignal,
  signal,
  AfterViewInit,
  ChangeDetectionStrategy,
  model,
  ViewChild,
  Input,
  OnDestroy,
  effect
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, SortDirection } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { TruncatePipe } from '@k12/pipes';
import { ExtendedTask, ProviderInfoStatus, SubTask, TaskStatus, TaskTableSelection, TaskType } from '@k12/models';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { dateRangeValidator } from '@k12/validators';
import { DateRangePickerComponent, DateRangeResult } from '@k12/forms';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatTooltipModule } from '@angular/material/tooltip';

// Constants
const SUBTASK_THRESHOLD = 0; // Expand is shown when subTasks.length > SUBTASK_THRESHOLD (i.e. 1+)


// Status icon configuration interface
export interface StatusIconConfig {
  statusName: string;
  iconName: string;
  iconType: 'material' | 'svg' | 'custom';
  color: string;
  svgPath?: string; // For custom SVG icons
}

// Extended SubTask for display purposes
export interface DisplayableSubTask extends SubTask {
  completed?: boolean;
  expanded?: boolean;
  parentDueDate?: Date;
}

enum ColumnWidths {
  action = 20,
  date = 10,
  description = 32,
  status = 17,
  type = 18
}

enum ExpandedColumnWidths {
  action = 4,
  dueDate = 18.5,
  date = 10,
  description = 33,
  status = 16.5,
  type = 17
}

// Default status icon mappings
const DEFAULT_STATUS_ICON_MAPPINGS: StatusIconConfig[] = [
  // Material icons come from here: https://fonts.google.com/icons
  {
    statusName: 'Not Received',
    iconName: 'radio_button_unchecked',
    iconType: 'material',
    color: '#9E9E9E'
  },
  {
    statusName: 'Under Review',
    iconName: 'search',
    iconType: 'material',
    color: '#00A8FA'
  },
  {
    statusName: 'Denied',
    iconName: 'error',
    iconType: 'material',
    color: '#B40A00'
  },
  {
    statusName: 'Resubmit',
    iconName: 'warning',
    iconType: 'material',
    color: '#F5D942'
  },
  {
    statusName: 'Past Due',
    iconName: 'error',
    iconType: 'material',
    color: '#B40A00'
  },
  {
    statusName: 'Accepted',
    iconName: 'check_circle',
    iconType: 'material',
    color: '#5C9E47'
  }
];

@Component({
  selector: 'lib-task-grid',
  templateUrl: './task-grid.component.html',
  styleUrls: ['./task-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    CommonModule,
    CdkTableModule,
    CfiMatTableComponent,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TruncatePipe,
    DateRangePickerComponent,
    NgxMatSelectSearchModule,
    MatTooltipModule
  ],
  schemas: [NO_ERRORS_SCHEMA]
})
export class TaskGridComponent implements AfterViewInit, /* OnChanges, */ OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @Output() tableColumnWidthToggled = new EventEmitter<TaskTableSelection>();
  @Output() taskSelected = new EventEmitter<DisplayableSubTask>();
  @Output() cbPaginate = new EventEmitter<{ pageSize?: number; pageIndex?: number }>();
  @Output() sortChanged = new EventEmitter<{ sortBy?: string; sortDirection: SortDirection }>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() applyFilterButtonClick = new EventEmitter<{
    taskStatus?: string;
    taskType?: string;
    dateRange?: DateRangeResult;
  }>();

  public columnWidths = ColumnWidths;
  public defaultPageSize = input.required<number>();
  @Input() totalPages: number = 1;
  @Input() currentPageNumber: number = 1;
  @Input() pageSize: number = 10;
  @Input() totalCount: number = 0;
  @Input() loading: boolean = false; // parent controls loading
  public noSearch = input<boolean>(false);
  public originalTasks = model.required<ExtendedTask[]>();

  @Input() status: ProviderInfoStatus | undefined;
  ProviderInfoStatus: typeof ProviderInfoStatus = ProviderInfoStatus;

  taskFilterForm: FormGroup;
  public searchInput: string = '';
  @Input() selectedTableSelection: TaskTableSelection = TaskTableSelection.toDo;
  public sortDirection = input.required<SortDirection>();
  public sortBy = input.required<string>();
  public statuses = input.required<TaskStatus[]>();
  public taskTypes = input.required<TaskType[]>();
  public tableSelection = TaskTableSelection;
  public tasks: WritableSignal<ExtendedTask[]> = signal([] as ExtendedTask[]);

  // Debounced search stream
  private readonly searchInputSubject = new Subject<string>();
  private readonly searchInputSubscription!: Subscription;

  // Required properties for table component
  public columnsToDisplayWithExpand: string[] = [];
  public displayedColumns: string[] = [];
  public expandedElement: ExtendedTask | null = null;
  public selectedTask: ExtendedTask | null = null;
  public columnDefinitions: ColumnDefinition[] = [
    {
      name: 'taskStatus',
      header: 'Task Status',
      sortable: true,
      width: ColumnWidths.status,
      expandedWidth: ExpandedColumnWidths.status,
      expandable: true,
      toggleExpand: true
    },
    {
      name: 'taskType',
      header: 'Task Name',
      sortable: true,
      width: ColumnWidths.type,
      expandedWidth: ExpandedColumnWidths.type,
      expandable: true
    },
    {
      name: 'description',
      header: 'Task Description',
      sortable: true,
      width: ColumnWidths.description,
      expandedWidth: ExpandedColumnWidths.description,
      expandable: true
    },
    {
      name: 'dateCreated',
      header: 'Date Assigned',
      sortable: true,
      width: ColumnWidths.date,
      expandedWidth: ExpandedColumnWidths.date,
      expandable: true
    },
    {
      name: 'dueDate',
      header: 'Due Date',
      sortable: true,
      width: ColumnWidths.date,
      expandedWidth: ExpandedColumnWidths.dueDate,
      expandable: true
    },
    {
      name: 'action',
      header: 'Action',
      sortable: false,
      action: true,
      width: ColumnWidths.action,
      expandedWidth: ExpandedColumnWidths.action,
      expandable: true
    }
  ];

  // Task status related properties
  private readonly statusIconMappings: StatusIconConfig[] = [...DEFAULT_STATUS_ICON_MAPPINGS];

  private readonly router = inject(Router);

  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly fb = inject(FormBuilder);

  constructor() {
    // Register the edit icon
    this.matIconRegistry.addSvgIcon(
      'k12-edit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/edit.svg')
    );
    this.taskFilterForm = this.fb.group({
      taskTypeId: [null],
      taskStatusId: [null],
      dateRange: this.fb.group(
        {
          startDate: [null as string | null],
          endDate: [null as string | null]
        },
        { validators: dateRangeValidator() }
      )
    });

    this.initializeStatusIconMappings();

    // Load page size from localStorage
    const savedPageSize = localStorage.getItem('task_grid_page_size');
    this.pageSize = savedPageSize ? Number.parseInt(savedPageSize) : 10;

    // Set up debounced search emission
    // Internal Subject; cannot error by construction, so no error handler is needed.
    this.searchInputSubscription = this.searchInputSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => {
        this.searchInput = value;
        this.searchChanged.emit(this.searchInput);
      });

    // react to server-side updates delivered via the input signal
    effect(() => {
      const items = this.originalTasks();
      if (items && Array.isArray(items)) {
        this.initializeTasks();
      }
    });
  }

  private initializeStatusIconMappings() {
    // Register any custom SVG icons here if needed
    // Example:
    // this.matIconRegistry.addSvgIcon(
    //   'custom-status-icon',
    //   this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/custom-status.svg')
    // );
  }

  getStatusIconConfig(statusName: string): StatusIconConfig | null {
    return this.statusIconMappings.find((config) => config.statusName === statusName) || null;
  }

  getStatusIconConfigById(taskStatusId: number): StatusIconConfig | null {
    const status = this.getTaskStatus(taskStatusId);
    if (!status) return null;
    return this.getStatusIconConfig(status.name);
  }

  get completeStatusId(): number | null {
    return this.statuses().find((status) => status.representsFinalStatus)?.taskStatusId || null;
  }

  get completeStatusIds(): number[] {
    return (
      this.statuses()
        .filter((status) => status.representsFinalStatus)
        ?.map((status) => status.taskStatusId) || []
    );
  }

  ngAfterViewInit() {
    // Set up the displayed columns
    this.displayedColumns = this.columnDefinitions.map((col) => col.name);
    this.columnsToDisplayWithExpand = [...this.displayedColumns];
    this.initializeTasks();
  }

  onTaskDateRangeFilterChange(event: DateRangeResult): void {
    this.taskFilterForm.get('dateRange')?.patchValue({
      startDate: event.startDate,
      endDate: event.endDate
    });
  }

  applyFilter(): void {
    this.applyFilterButtonClick.emit({
      taskType: this.taskFilterForm.get('taskTypeId')?.value,
      taskStatus: this.taskFilterForm.get('taskStatusId')?.value,
      dateRange: this.taskFilterForm.get('dateRange')?.value
    });
  }

  clearFilters(): void {
    this.taskFilterForm.patchValue({
      taskTypeId: null,
      taskStatusId: null,
      dateRange: {
        startDate: null,
        endDate: null
      }
    });
    this.applyFilter();
  }

  private initializeTasks() {
    this.toggleDataSource();
  }

  private isSubTaskCompleted(subTask: SubTask): boolean {
    return this.isItemCompleted(subTask.taskStatusId, subTask.taskStatus);
  }

  private isItemCompleted(taskStatusId: number, taskStatus: string): boolean {
    // Use dynamic statuses if available, otherwise fall back to string matching
    if (this.statuses && this.statuses().length > 0) {
      const finalStatusIds = this.completeStatusIds;
      return finalStatusIds.includes(taskStatusId);
    }

    // Fallback to string matching for complete statuses
    const completedStatuses = ['Accepted', 'Denied'];
    return completedStatuses.includes(taskStatus);
  }

  getTaskStatus(taskStatusId: number): TaskStatus | undefined {
    return this.statuses().find((status) => status.taskStatusId === taskStatusId);
  }

  /**
   * Determines if a task should show the edit button.
   * Edit button is hidden if the task has child rows.
   */
  public shouldShowEditButton(task: ExtendedTask): boolean {
    const taskStatus = this.getTaskStatus(task.taskStatusId);
    const isEditableByRecipient = taskStatus?.isEditableByProgramPartner || false;
    const hasMultipleSubtasks = !!task.expandable || (task.subTasks?.length ?? 0) > SUBTASK_THRESHOLD;

    return isEditableByRecipient && !hasMultipleSubtasks;
  }

  public shouldSubShowEditButton(task: SubTask): boolean {
    const taskStatus = this.getTaskStatus(task.taskStatusId);
    return taskStatus?.isEditableByProgramPartner || false;
  }

  toggleTabSelection(tableSelection: TaskTableSelection) {
    // Reset filter form and emit event to parent to update data source based on new selection
    this.taskFilterForm.reset();
    this.tableColumnWidthToggled.emit(tableSelection);
  }

  getBorderWidth(tableSelection: TaskTableSelection): string {
    return tableSelection === this.selectedTableSelection ? '4px' : '0px';
  }

  isSelected(tableSelection: TaskTableSelection): boolean {
    return tableSelection === this.selectedTableSelection;
  }

  public updateSearch(value: string) {
    this.searchInputSubject.next(value);
  }

  private FilterTasks(): void {
    const filterValue = this.searchInput?.trim().toLowerCase() || '';
    const filteredTasks = this.originalTasks().map((task) => {
      const originalSubTasks = task.subTasks || [];
      let filteredSubTasks = originalSubTasks;
      const isExpandable = originalSubTasks.length > SUBTASK_THRESHOLD;
      if (filterValue) {
        filteredSubTasks = originalSubTasks.filter((subTask) => this.subTaskMatchesSearch(subTask, filterValue));

        //the search match was only at the task level, include all subtasks
        if (filteredSubTasks.length === 0) {
          filteredSubTasks = originalSubTasks;
        }
      }

      // Update subCells for table rendering
      const filteredSubCells = isExpandable
        ? filteredSubTasks.map((subTask) => ({
            ...subTask,
            parentDueDate: task.dueDate
          }))
        : [];
      return {
        ...task,
        subTasks: filteredSubTasks,
        subCells: filteredSubCells,
        expandable: isExpandable
      };
    });
    this.tasks.set(filteredTasks);
  }

  private subTaskMatchesSearch(subTask: SubTask, filterValue: string | undefined): boolean {
    if (!filterValue || filterValue.trim() === '') return true;
    const lowerFilter = filterValue.toLowerCase();
    return (
      (subTask.name?.toLowerCase().includes(lowerFilter) ?? false) ||
      (subTask.taskStatus?.toLowerCase().includes(lowerFilter) ?? false)
    );
  }

  handleTaskButtonClicked(taskOrSubTask: ExtendedTask | SubTask) {
    // Emit task/subtask selection event for parent component to handle
    this.taskSelected.emit(taskOrSubTask as DisplayableSubTask);

    // Optional default navigation - parent can override by listening to taskSelected event
    if (!this.taskSelected.observed) {
      this.router.navigate(['/task', taskOrSubTask.id]).then();
    }
  }

  onExpandedElementChange(element: ExtendedTask | null) {
    this.expandedElement = element;

    // Update the expanded property on the task
    if (element) {
      element.expanded = true;
    } else {
      // Reset expanded state on all tasks
      this.tasks().forEach((task) => {
        task.expanded = false;
      });
    }
  }

  isExpanded(task: ExtendedTask): boolean {
    return task === this.expandedElement;
  }

  selectTask(task: ExtendedTask) {
    this.selectedTask = task;
  }

  toggleDataSource() {
    if (!this.originalTasks() || this.originalTasks().length === 0) {
      this.tasks.set([]);
      return;
    }

    this.FilterTasks();
  }

  // Helper methods for template to check and get child tasks
  hasChildTasks(parentTask: ExtendedTask): boolean {
    return !!parentTask.expandable || !!(parentTask.subTasks && parentTask.subTasks.length > SUBTASK_THRESHOLD);
  }

  getChildTasks(parentTask: ExtendedTask): SubTask[] {
    return parentTask.subTasks || [];
  }

  // Helper method to check if a subtask is completed
  isChildTaskCompleted(childTask: SubTask): boolean {
    return this.isSubTaskCompleted(childTask);
  }

  // Handle pagination events
  public updatePaging($event: { pageSize?: number; pageIndex?: number }): void {
    const newPageSize = $event?.pageSize;
    if (newPageSize) {
      localStorage.setItem('task_grid_page_size', newPageSize.toString());
    }
    this.cbPaginate.emit($event);
  }

  public updateSort($event: { active?: string; direction?: string }): void {
    this.sortChanged.emit({ sortBy: $event?.active, sortDirection: $event?.direction as SortDirection });
  }

  ngOnDestroy(): void {
    if (this.searchInputSubscription) {
      this.searchInputSubscription.unsubscribe();
    }
  }
}
